import { errorCopy } from '@/api/errorCopy';
/**
 * Pre-flight before any AI naming call (TMMAPP-0032). Offers the classifier's suggested
 * exclusions first, says the requests (never money: TMMAPP-0162 item 9), then PATCHes exclusions
 * and calls `run()`. Cancel makes no call. This is the only UI path that may invoke
 * `payeeSuggestionsApi.run`.
 *
 * TMMAPP-0163: the estimate is the whole job (`estimatedCallCount`, item 1). Continue starts a
 * background run (`202 {runId}`, or the run already in flight) and hands its id to the host, which
 * shows its progress (item 2). Under FREE_FIRST, a line under the estimate offers "Use AI for all
 * naming": the choice replaces this body in place (DLG-005) and Save returns here, where Continue is
 * the run it suggests (item 6); nothing runs from the switch itself.
 *
 * When AI can't run (TMMAPP-0123 item 7) — the host's status says `aiAvailable` false, or a run
 * was refused — Continue is replaced: the reason's set-up action shows (Turn on AI for `USER`;
 * Enable or Top up through `AiWalletPrompt` for a wallet reason) and Cancel reads Close; with no
 * action (`NO_PRICE`, the gate's other reasons) only Close is left. No run is attempted.
 */
import { useEffect, useId, useState } from 'react';
import {
  AI_WALLET_REASONS,
  aiRefusal,
  isAiWalletRefusal,
  type AiRefusal,
  type AiWalletReason,
} from '@/api/errors';
import { patchMe } from '@/api/settings';
import { aiEnabledPatch } from '@/features/ai/aiLimitPatch';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { useSubscriberAction } from '@/session/useSubscriberAction';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { TOP_UP_TITLE_ID, useTopUpSwap } from '@/features/aiWallet/useTopUpSwap';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { payeesApi, type Payee } from '@/api/payees';
import { payeeSuggestionsApi } from '@/api/payeeSuggestions';
import { ErrorState } from '@/components/ErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { counterpartyReasonText } from './counterpartyReason';
import { NamingModeBody } from './NamingModeChoice';

export interface AiNamingPreflightProps {
  open: boolean;
  estimatedCallCount: number;
  onClose: () => void;
  /** Continue started a run (or answered the one in flight): the host follows it. */
  onStarted: (runId: number) => void | Promise<void>;
  /** The naming mode changed here: the host re-reads its numbers (the estimate). */
  onModeChanged?: (() => void) | undefined;
  /** The host's naming status: false means no run can succeed, so none is offered. */
  aiAvailable?: boolean | undefined;
  /** Why AI isn't available (the status's `aiUnavailableReason`). */
  aiUnavailableReason?: string | null | undefined;
  /**
   * A run was refused: true when the host takes it over (it closes the dialog and says why on its
   * own surface; TMMAPP-0136 item 4c); false keeps the refusal here.
   */
  onRefused?: ((reason: string) => boolean) | undefined;
}

/** The gate's reasons the catalog words (`naming.reason.*`); anything else reads as the fallback. */
const PLAIN_REASONS = new Set(['GLOBAL', 'FEATURE', 'USER', 'DAILY_CAP', 'FORBIDDEN']);

function walletReason(reason: string | null | undefined): AiWalletReason | null {
  return (AI_WALLET_REASONS as readonly (string | null | undefined)[]).includes(reason)
    ? (reason as AiWalletReason)
    : null;
}

export function AiNamingPreflight({
  open,
  estimatedCallCount,
  onClose,
  onStarted,
  onModeChanged,
  aiAvailable = true,
  aiUnavailableReason = null,
  onRefused,
}: AiNamingPreflightProps) {
  const { t } = useTranslation();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const [turningOn, setTurningOn] = useState(false);
  const [unblocked, setUnblocked] = useState(false);
  const [suggested, setSuggested] = useState<Payee[]>([]);
  const [ticked, setTicked] = useState<ReadonlySet<number>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletRefusal, setWalletRefusal] = useState<
    (AiRefusal & { reason: AiWalletReason }) | null
  >(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { swap: topUpSwap, swapped: toppingUp, attachPanelHost, requestHostClose } = useTopUpSwap();
  // "Use AI for all naming" replaces the body with the choice (DLG-005).
  const [choosingMode, setChoosingMode] = useState(false);
  const modeTitleId = useId();
  const openSubscribe = useOpenSubscribe();
  const chooseMode = useSubscriberAction(
    () => setChoosingMode(true),
    false,
    // The purchase experience closes this dialog first (DLG-005).
    () => {
      onClose();
      openSubscribe();
    },
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setError(null);
      setWalletRefusal(null);
      setLoadFailure(null);
      setBusy(false);
      setUnblocked(false);
      setChoosingMode(false);
      void payeesApi
        .list({ status: 'ACTIVE' })
        .then((rows) => {
          if (cancelled) return;
          const candidates = rows.filter(
            (row) => row.suggestedAiExcluded === true && !row.aiExcluded,
          );
          setSuggested(candidates);
          setTicked(new Set(candidates.map((row) => row.id)));
        })
        .catch((failure: unknown) => {
          if (!cancelled) setLoadFailure({ error: failure });
        });
    });
    return () => {
      cancelled = true;
    };
  }, [open, loadAttempt]);

  function toggle(id: number) {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // The host says AI can't run, and the person hasn't just set it up here (turned on, enabled).
  const hostBlocked = !aiAvailable && !unblocked;
  const hostWallet = hostBlocked ? walletReason(aiUnavailableReason) : null;
  const shownRefusal: (AiRefusal & { reason: AiWalletReason }) | null =
    walletRefusal ??
    (hostWallet !== null
      ? { reason: hostWallet, availableMinor: null, firstPurchaseRequired: false, debtMinor: null }
      : null);
  const blocked = hostBlocked || walletRefusal !== null;

  async function turnOnAi() {
    if (turningOn) return;
    setTurningOn(true);
    setError(null);
    try {
      const patch = aiEnabledPatch(me.user.aiEnabled, true);
      if (Object.keys(patch).length > 0) await patchMe(patch);
      await refetchMe();
      setUnblocked(true);
    } catch (err) {
      setError(errorCopy(t, err, 'accounts.errorGeneric'));
    } finally {
      setTurningOn(false);
    }
  }

  function handleContinue() {
    if (blocked) return;
    void runNaming();
  }

  async function runNaming() {
    if (busy) return;
    setBusy(true);
    setError(null);
    setWalletRefusal(null);
    try {
      const ordered = suggested.filter((row) => ticked.has(row.id));
      for (const row of ordered) {
        await payeesApi.patch(row.id, { aiExcluded: true });
      }
      const { runId } = await payeeSuggestionsApi.run();
      await onStarted(runId);
    } catch (err) {
      const refusal = aiRefusal(err);
      // A bare `AI_DISABLED` (no wallet reason) is its own reason; the host may take it over.
      if (refusal !== null && onRefused?.(refusal.reason ?? 'AI_DISABLED') === true) return;
      if (isAiWalletRefusal(refusal)) {
        setWalletRefusal(refusal);
        return;
      }
      const message = errorCopy(t, err, 'accounts.errorGeneric');
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      // While the top-up shows, the backdrop and Esc are its Cancel (TMMAPP-0119 item 16).
      onClose={() =>
        requestHostClose(() => {
          if (!busy) onClose();
        })
      }
      fullWidth
      maxWidth="sm"
      aria-labelledby={
        toppingUp ? TOP_UP_TITLE_ID : choosingMode ? modeTitleId : 'ai-naming-preflight-title'
      }
    >
      {choosingMode && !toppingUp && (
        <NamingModeBody
          initial="AI_ALL"
          titleId={modeTitleId}
          onCancel={() => setChoosingMode(false)}
          onSaved={() => {
            setChoosingMode(false);
            onModeChanged?.();
          }}
        />
      )}
      {/* TOP UP swaps this body for the top-up in place (DLG-005, TMMAPP-0118 C11). */}
      <Box sx={{ display: toppingUp || choosingMode ? 'none' : 'contents' }}>
        <DialogTitle id="ai-naming-preflight-title">{t('naming.preflightTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {loadFailure !== null && (
              <ErrorState
                variant="section"
                error={loadFailure.error}
                what={t('errors.what.namingPreflight')}
                onRetry={() => setLoadAttempt((n) => n + 1)}
              />
            )}
            {error !== null && <Alert severity="error">{error}</Alert>}
            {shownRefusal !== null && (
              <AiWalletPrompt
                refusal={shownRefusal}
                onTryAgain={() => {
                  // The prompt's own retry (after a top-up, or BUSY): the run is asked again.
                  setWalletRefusal(null);
                  if (!hostBlocked) void runNaming();
                }}
                onCloseHost={onClose}
                swap={topUpSwap}
                onCharged={() => {
                  if (hostBlocked) setUnblocked(true);
                }}
              />
            )}
            {hostBlocked && hostWallet === null && (
              <Alert severity="info" data-ai-unavailable="">
                {t('naming.aiUnavailable', {
                  reason:
                    aiUnavailableReason !== null && PLAIN_REASONS.has(aiUnavailableReason)
                      ? t(`naming.reason.${aiUnavailableReason}` as 'naming.reason.fallback')
                      : t('naming.reason.fallback'),
                })}
              </Alert>
            )}
            {hostBlocked && aiUnavailableReason === 'USER' && (
              <SubmitButton
                loading={turningOn}
                onClick={() => void turnOnAi()}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('genesis.turnOnAi')}
              </SubmitButton>
            )}

            <Typography variant="subtitle2" component="h3">
              {t('naming.reviewFirst')}
            </Typography>
            {suggested.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('naming.noneSuggested')}
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {suggested.map((row) => (
                  <FormControlLabel
                    key={row.id}
                    control={
                      <Checkbox
                        checked={ticked.has(row.id)}
                        onChange={() => toggle(row.id)}
                        disabled={busy}
                      />
                    }
                    label={
                      <Stack spacing={0}>
                        <Typography>{row.name}</Typography>
                        {row.suggestionReason !== null && (
                          <Typography
                            variant="caption"
                            data-counterparty-reason=""
                            sx={{ color: 'text.secondary' }}
                          >
                            {counterpartyReasonText(t, row.suggestionReason)}
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                ))}
              </Stack>
            )}

            <Typography data-naming-cost="">
              {t('naming.cost', { count: estimatedCallCount })}
            </Typography>
            {me.user.payeeNamingMode === 'FREE_FIRST' && (
              <Typography variant="body2" data-naming-mode-offer="">
                <Trans
                  i18nKey="naming.mode.preflightLine"
                  components={{
                    mode: (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        disabled={busy}
                        onClick={chooseMode.run}
                        sx={{ verticalAlign: 'baseline' }}
                      />
                    ),
                  }}
                />
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            {blocked ? t('common.close') : t('naming.cancel')}
          </Button>
          {!blocked && (
            <SubmitButton
              onClick={handleContinue}
              disabled={busy || loadFailure !== null}
              loading={busy}
            >
              {t('naming.continue')}
            </SubmitButton>
          )}
        </DialogActions>
      </Box>
      <Box ref={attachPanelHost} sx={{ display: 'contents' }} data-top-up-host="" />
    </Dialog>
  );
}
